import { publicEnv } from "@/lib/public-env";
import { isKnownWalletId } from "@/lib/wallet/solana/quirks";

import type { KnownWalletId, WalletId } from "@/lib/wallet/solana/types";

/**
 * Which Solana wallets a build offers, on `NEXT_PUBLIC_MUDWATER_SOLANA_WALLETS`
 * (`optionc-plan.md` §4). One rule:
 *
 * - **Unset (or empty):** legacy. Today's `PhantomWalletBridge` runs and none
 *   of the generic layer is mounted.
 * - **Any value:** the generic layer, for every wallet listed, Phantom
 *   included. `all` offers every known wallet and any other Wallet Standard
 *   Solana wallet; otherwise a comma list of exact, lowercase ids.
 *
 * Glyph login never changes this external-wallet setting.
 */
export type SolanaWalletsSetting =
  | { mode: "legacy" }
  | { mode: "all" }
  | { mode: "list"; ids: readonly KnownWalletId[]; unknown: readonly string[] };

/** Read from any source, so the build guard can judge any environment. */
export function parseSolanaWallets(
  value: string | undefined
): SolanaWalletsSetting {
  const raw = value?.trim() ?? "";
  if (raw === "") return { mode: "legacy" };
  if (raw === "all") return { mode: "all" };
  const listed = raw.split(",").map((part) => part.trim());
  return {
    mode: "list",
    ids: listed.filter(isKnownWalletId),
    unknown: listed.filter((part) => part !== "" && !isKnownWalletId(part)),
  };
}

/**
 * The wallets production may list (code-reviewed). Production stays unset
 * (legacy) until the user cuts over; the cut-over is `phantom`. Phantom is
 * admitted with the parity suite that holds its generic path to the legacy
 * one (`phantom-parity.*.test.ts`); any other wallet only after its own local
 * and staging checks (D3).
 */
export const VERIFIED: ReadonlySet<KnownWalletId> = new Set<KnownWalletId>([
  "phantom",
]);

/** This build's setting. `NEXT_PUBLIC_*` is inlined only when read directly. */
function setting(): SolanaWalletsSetting {
  return parseSolanaWallets(publicEnv("NEXT_PUBLIC_MUDWATER_SOLANA_WALLETS"));
}

/** Whether the generic layer runs at all. */
export function isGenericEnabled(): boolean {
  return setting().mode !== "legacy";
}

/** Whether a known wallet is offered. `other:*` wallets are offered by `offersUnknown`. */
export function isOffered(id: WalletId): boolean {
  const current = setting();
  if (current.mode === "legacy") return false;
  if (current.mode === "all") return true;
  return isKnownWalletId(id) && current.ids.includes(id);
}

/** Whether Wallet Standard wallets the quirk table does not know are offered. */
export function offersUnknown(): boolean {
  return setting().mode === "all";
}

/**
 * Wallets whose Solana use makes "Browser Wallet" on the EVM side a hazard:
 * their extension answers the EVM side once Solana is connected, or revokes
 * Solana with it. Phantom is not one: its own named entry is gated, and PR 1
 * withdraws Browser Wallet in front of it.
 */
const GATING: readonly KnownWalletId[] = [
  "okx",
  "trust",
  "backpack",
  "bitget",
  "exodus",
  "brave",
  "coinbase",
  "solflare",
  "metamask",
];

/**
 * Whether Browser Wallet is gated (`chosenOnly`) in this build (D6, R1). Off
 * under `phantom` alone, so the cut-over changes nothing on the EVM side.
 * MetaMask alone turns it on: its shared permission makes Browser Wallet in
 * front of it the same hazard. Unknown wallets are offered only under `all`,
 * which offers every one of these too, so they need no case of their own.
 */
export function gatesBrowserWallet(): boolean {
  return GATING.some((id) => isOffered(id));
}
